// Section inspector fields: the per-type editors (SectionFields), grouped the
// way Flowmark groups them (a "Content" group that is always open, then
// Media, Button and Social proof groups that fold), the section's Style group
// (SectionStyleFields, closed by default), the calendar picker, and
// the option lists behind the style controls (animations, text-sizable types).
// SectionTab renders these under its head row. The building blocks (labeled
// fields, segmented choices, groups, item cards) are in ./InspectorKit.
import { useEffect, useState } from 'react';
import { isHttpsUrl, embedCodeToUrl, isScriptEmbed, etWallToIso, isoToEtWall } from '../model/pages';
import type {
  FaqItem, FeatureItem, PricingPlan, Section, SectionAnimation, SectionTextSize, SectionType, StepItem, TeamMember,
} from '../model/pages';
import type { CrmCalendarSummary } from '../api';
import ImageInput from './ImageInput';
import { useEditorApi } from './editorApi';
import { BackgroundPositionField, SectionDesignFields } from './SectionDesignFields';
import { FooterLayoutFields } from './SectionAlignmentFields';
import { resetSectionStylePatch } from './sectionStyle';
import {
  AnchorField, AttributionForwardingField, BandPicker, BannerFields, BioFields, HighlightField, IconSelect, InsetField, ModulesFields, NavFields, NoteField,
  NumberRows, OfferFields, ProofFields, ReviewsFields, VariantPicker,
} from './FunnelFields';
import {
  AddButton, Block, BTN, Check, Choice, ItemList, LinkList, RangeField, SelectField, StringList, TextAreaField, TextField, fileLabel,
} from './InspectorKit';

type On = (p: Partial<Section>) => void;

// Section entrance animations, in display order. 'fade-up' is stored as undefined
// (it is the default every existing page already has).
export const ANIMATION_CHOICES: ReadonlyArray<[SectionAnimation, string]> = [
  ['fade-up', 'Fade up'], ['fade-in', 'Fade in'], ['slide-left', 'Slide left'],
  ['slide-right', 'Slide right'], ['zoom', 'Zoom'], ['none', 'None'],
];

// Types whose text the "Text size" control scales (SectionRenderer's map).
export const TEXT_SIZABLE: SectionType[] = ['hero', 'text', 'benefits', 'compare', 'features', 'stats', 'steps', 'testimonial', 'testimonials', 'faq', 'cta', 'pricing', 'guarantee', 'split', 'modules', 'bio', 'offer'];

const TEXT_SIZES = [['sm', 'S'], ['md', 'M'], ['lg', 'L'], ['xl', 'XL']] as const;
const SPACES = [['auto', 'Auto'], ['tight', 'Tight'], ['normal', 'Normal'], ['loose', 'Loose']] as const;
const SIDES = [['left', 'Left'], ['right', 'Right']] as const;
const LIST_STYLES = [['check', 'Check'], ['number', 'Numbers']] as const;
const FORM_LAYOUTS = [['card', 'Form only'], ['stacked', 'Text above form'], ['split', 'Text beside form']] as const;
const DIVIDER_SIZES = [['sm', 'Small'], ['md', 'Medium'], ['lg', 'Large']] as const;
const IMAGE_POS = [['side', 'Side'], ['below', 'Below'], ['background', 'Background']] as const;
const VIDEO_POS = [['side', 'Side'], ['below', 'Below']] as const;

/** Placeholder for fields a section works fine without. */
const OPTIONAL = 'Optional';

/** "Text size" (S / M / L / XL) for the text-bearing section types. Absent/md =
 *  today's sizes. */
export function TextSizeSeg({ value, onChange }: { value: SectionTextSize | undefined; onChange: (z: SectionTextSize) => void }) {
  return (
    <Choice label="Text size" value={value ?? 'md'} options={TEXT_SIZES} onChange={onChange}
      itemLabel={(l) => `Text size ${l}`} />
  );
}

/** Per-section background image + overlay (ClickFunnels/GHL-style). Any section
 *  can sit on a full-bleed image/SVG with an adjustable dark scrim. */
export function BackgroundImageField({ section, onChange }: { section: Section; onChange: On }) {
  const overlay = section.bg_overlay ?? 45;
  return (
    <div className="grid min-w-0 gap-3.5">
      <ImageInput label="Background image" value={section.bg_image_url ?? ''}
        onChange={(url) => onChange({ bg_image_url: url })} />
      {section.bg_image_url && (
        <>
          <RangeField label="Overlay" value={overlay} min={0} max={100} display={`${overlay}%`}
            onChange={(v) => onChange({ bg_overlay: v })} />
          <BackgroundPositionField section={section} onChange={onChange} />
        </>
      )}
    </div>
  );
}

/** The section's Style group, closed by default: background band, rounded
 *  panel, text size, space above and below, entrance animation, background
 *  image and anchor. SectionTab renders it after SectionFields, for every
 *  section except the footer. Patches match the inline controls it replaced. */
export function SectionStyleFields({ section, onChange, onCopyStyle, onPasteStyle, onResetStyle, canPasteStyle = false }: {
  section: Section; onChange: On; onCopyStyle?: () => void; onPasteStyle?: () => void; onResetStyle?: () => void; canPasteStyle?: boolean;
}) {
  return (
    <>
      <Block name="Style">
        <BandPicker value={section.bg} onChange={(bg) => onChange({ bg })} />
        <InsetField section={section} onChange={onChange} />
        {TEXT_SIZABLE.includes(section.type) && (
          <TextSizeSeg value={section.text_size} onChange={(z) => onChange({ text_size: z })} />
        )}
        <Choice label="Space above" value={section.space ?? 'auto'} options={SPACES}
          onChange={(sp) => onChange({ space: sp === 'auto' ? undefined : sp })} />
        <Choice label="Space below" value={section.space_below ?? 'auto'} options={SPACES}
          onChange={(sp) => onChange({ space_below: sp === 'auto' ? undefined : sp })} />
        <Choice label="Animation" value={section.animation ?? 'fade-up'} options={ANIMATION_CHOICES}
          onChange={(a) => onChange({ animation: a === 'fade-up' ? undefined : a })} />
        <BackgroundImageField section={section} onChange={onChange} />
        <AnchorField section={section} onChange={onChange} />
        <div className="pe-section-style-actions">
          {onCopyStyle && <button type="button" className={BTN} onClick={onCopyStyle}>Copy style</button>}
          {onPasteStyle && <button type="button" className={BTN} onClick={onPasteStyle} disabled={!canPasteStyle}>Paste style</button>}
          <button type="button" className={BTN} onClick={onResetStyle ?? (() => onChange(resetSectionStylePatch()))}>Reset section style</button>
        </div>
      </Block>
      <SectionDesignFields section={section} onChange={onChange} />
    </>
  );
}

// Dropdown of the CRM's calendars: the easy way to drop a booking calendar
// onto a page (renders /book/<calendar slug>?embed=1). A calendar that is off
// still shows, marked, so a page that uses one keeps its value.
export function BookingPicker({ value, onChange }: { value: string; onChange: (slug: string) => void }) {
  const api = useEditorApi();
  const [calendars, setCalendars] = useState<CrmCalendarSummary[]>([]);
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    let on = true;
    api.listCrmCalendars()
      .then((list) => { if (on) { setCalendars(Array.isArray(list) ? list : []); setLoaded(true); } })
      .catch(() => { if (on) setLoaded(true); });
    return () => { on = false; };
  }, [api]);
  const known = !value || calendars.some((c) => c.slug === value);
  return (
    <SelectField label="Calendar" value={value} onChange={onChange}
      hint={<>Manage calendars in <a href="/crm/bookings" target="_blank" rel="noreferrer" className="underline">CRM bookings</a>.</>}>
      <option value="">{loaded && calendars.length === 0 ? 'No calendars yet. Create one in CRM bookings.' : 'Choose a calendar'}</option>
      {loaded && !known && <option value={value}>{value} (not found)</option>}
      {calendars.map((c) => <option key={c.slug} value={c.slug}>{c.name}{c.active ? '' : ' (off)'}</option>)}
    </SelectField>
  );
}

// Optional section header (small label + heading + supporting text) shown above
// a collection section (features/stats/pricing/faq/steps/gallery/team/logos) so
// the grid reads as a titled landing-page section. Empty fields = no header.
function HeaderFields({ section, onChange }: { section: Section; onChange: On }) {
  return (
    <>
      <TextField dynamic label="Small label above" placeholder={OPTIONAL} value={section.eyebrow ?? ''} onChange={(v) => onChange({ eyebrow: v })} />
      <TextAreaField dynamic singleLine label="Heading" placeholder={OPTIONAL} value={section.heading ?? ''} onChange={(v) => onChange({ heading: v })} />
      <TextAreaField dynamic label="Supporting text" placeholder={OPTIONAL} value={section.subheadline ?? ''} onChange={(v) => onChange({ subheadline: v })} />
    </>
  );
}

/** The image + text section's button: no link, the form, the next step, or
 *  another website. "Another website…" stays picked while its link is still
 *  empty (the stored href is '' then, the same as "No link"). */
function SplitButtonAction({ section, onChange }: { section: Section; onChange: On }) {
  const href = section.href ?? '';
  const [customPicked, setCustomPicked] = useState(false);
  const derived = href === '' ? 'none' : href === '#form' ? '#form' : href === '#next' ? '#next' : 'custom';
  const mode = customPicked && href === '' ? 'custom' : derived;
  return (
    <>
      <SelectField label="When clicked, go to" value={mode}
        onChange={(v) => { setCustomPicked(v === 'custom'); onChange({ href: v === 'none' ? '' : v === 'custom' ? '' : v }); }}>
        <option value="none">No link</option>
        <option value="#form">The form (scrolls to it or opens it)</option>
        <option value="#next">The next step (#next)</option>
        <option value="custom">Another website…</option>
      </SelectField>
      {mode === 'custom' && (
        <TextField label="Link" placeholder="https://…" value={href}
          onChange={(v) => { setCustomPicked(true); onChange({ href: v }); }} />
      )}
    </>
  );
}

export function SectionFields({ section, onChange, multiStep }: { section: Section; onChange: On; multiStep?: boolean }) {
  const fields = fieldsFor(section, onChange, multiStep);
  if (!fields) return null;
  // Keyed by section so an open item card or a picked option never carries
  // over to the next section selected.
  return <div key={section.id} className="grid min-w-0">{fields}</div>;
}

function fieldsFor(section: Section, onChange: On, multiStep?: boolean) {
  switch (section.type) {
    case 'hero':
      return (
        <>
          <Block name="Content" collapsible={false}>
            <Check label="Show logo" checked={section.logo ?? false} onChange={(v) => onChange({ logo: v })} />
            <TextField dynamic label="Small label above" placeholder={OPTIONAL} value={section.eyebrow ?? ''} onChange={(v) => onChange({ eyebrow: v })} />
            <TextAreaField dynamic singleLine label="Headline" value={section.headline ?? ''} onChange={(v) => onChange({ headline: v })} />
            <HighlightField section={section} onChange={onChange} field="headline" />
            <TextAreaField dynamic label="Supporting text" placeholder={OPTIONAL} value={section.subheadline ?? ''} onChange={(v) => onChange({ subheadline: v })} />
          </Block>
          <Block name="Media" defaultOpen>
            <TextField label="Video link" placeholder="YouTube, Loom, Vimeo or Wistia link" value={section.video_url ?? ''}
              onChange={(v) => onChange({ video_url: v })} />
            <ImageInput label="Cover image" value={section.poster_url ?? ''}
              onChange={(url) => onChange({ poster_url: url || undefined })} />
            {!section.video_url && !section.poster_url && (
              <ImageInput label="Image" value={section.image_url ?? ''} onChange={(url) => onChange({ image_url: url })} />
            )}
            {((section.image_url && !section.video_url) || section.poster_url) && (
              <Choice label={section.poster_url ? 'Video position' : 'Image position'} value={section.image_pos ?? 'side'}
                options={section.poster_url ? VIDEO_POS : IMAGE_POS} onChange={(v) => onChange({ image_pos: v })} />
            )}
          </Block>
          <Block name="Button" defaultOpen>
            <TextField dynamic label="Button text" placeholder={OPTIONAL} value={section.cta_label ?? ''}
              onChange={(v) => onChange({ cta_label: v })} />
            {section.cta_label && (
              <TextField label="When clicked, go to" placeholder="#form, #book, #next or a URL" value={section.cta_href ?? ''}
                onChange={(v) => onChange({ cta_href: v })} />
            )}
            <TextField dynamic label="Second link text" placeholder={OPTIONAL} value={section.cta2_label ?? ''}
              onChange={(v) => onChange({ cta2_label: v })} />
            {section.cta2_label && (
              <TextField label="Second link goes to" placeholder="#book or a URL" value={section.cta2_href ?? ''}
                onChange={(v) => onChange({ cta2_href: v })} />
            )}
            <NoteField section={section} onChange={onChange} />
            <AttributionForwardingField section={section} onChange={onChange} />
          </Block>
          {/* Opens by default when the hero already shows a proof line; a stored
              open/closed choice wins once the group has been toggled. */}
          <Block name="Social proof" defaultOpen={!!(section.proof_text || section.proof_avatars?.length || section.rating)}>
            <ProofFields section={section} onChange={onChange} />
          </Block>
        </>
      );
    case 'text':
      return (
        <Block name="Content" collapsible={false}>
          <TextField dynamic label="Small label above" placeholder={OPTIONAL} value={section.eyebrow ?? ''} onChange={(v) => onChange({ eyebrow: v })} />
          <TextAreaField dynamic singleLine label="Heading" value={section.heading ?? ''} onChange={(v) => onChange({ heading: v })} />
          {section.heading && <HighlightField section={section} onChange={onChange} />}
          <TextAreaField dynamic label="Text" value={section.body ?? ''} onChange={(v) => onChange({ body: v })} />
        </Block>
      );
    case 'benefits':
      return (
        <Block name="Content" collapsible={false}>
          <VariantPicker section={section} onChange={onChange} />
          {section.variant && <HeaderFields section={section} onChange={onChange} />}
          <StringList dynamic label="Items" noun="item" placeholder="e.g. Save hours every week" items={section.items ?? []}
            onChange={(items) => onChange({ items })} />
          <Choice label="List style" value={section.list_style ?? 'check'} options={LIST_STYLES}
            onChange={(ls) => onChange({ list_style: ls })} />
        </Block>
      );
    case 'compare':
      return (
        <Block name="Content" collapsible={false}>
          <HeaderFields section={section} onChange={onChange} />
          <TextField dynamic label="Left column title" placeholder="e.g. The old way" value={section.left_title ?? ''}
            onChange={(v) => onChange({ left_title: v })} />
          <StringList dynamic label="Left column items" noun="item" items={section.left_items ?? []} onChange={(a) => onChange({ left_items: a })} />
          <TextField dynamic label="Right column title" placeholder="e.g. The new way" value={section.right_title ?? ''}
            onChange={(v) => onChange({ right_title: v })} />
          <StringList dynamic label="Right column items" noun="item" items={section.right_items ?? []} onChange={(a) => onChange({ right_items: a })} />
        </Block>
      );
    case 'stats':
      return (
        <Block name="Content" collapsible={false}>
          <VariantPicker section={section} onChange={onChange} />
          <HeaderFields section={section} onChange={onChange} />
          <NumberRows label="Stats" noun="stat" items={section.stats ?? []} onChange={(stats) => onChange({ stats })} />
        </Block>
      );
    case 'features':
      return (
        <Block name="Content" collapsible={false}>
          <VariantPicker section={section} onChange={onChange} />
          <HeaderFields section={section} onChange={onChange} />
          <ItemList<FeatureItem> label="Features" noun="feature" items={section.features ?? []}
            make={() => ({ icon: '', title: '', body: '' })} title={(f) => f.title} summary={(f) => f.body}
            onChange={(features) => onChange({ features })}>
            {(f, _i, set) => {
              const p = (x: Partial<FeatureItem>) => set({ ...f, ...x });
              return (
                <>
                  <div className={f.icon_name ? 'grid min-w-0' : 'grid min-w-0 grid-cols-[minmax(0,1fr)_5.5rem] gap-2'}>
                    <IconSelect value={f.icon_name} onChange={(name) => p({ icon_name: name })} />
                    {!f.icon_name && (
                      <TextField label="Text mark" placeholder="01" inputClassName="text-center" value={f.icon ?? ''}
                        onChange={(v) => p({ icon: v })} />
                    )}
                  </div>
                  <TextField dynamic label="Title" value={f.title} onChange={(v) => p({ title: v })} />
                  <TextAreaField dynamic label="Description" value={f.body ?? ''} onChange={(v) => p({ body: v })} />
                  <ImageInput label="Image (if no icon)" value={f.image ?? ''} onChange={(url) => p({ image: url })} />
                </>
              );
            }}
          </ItemList>
        </Block>
      );
    case 'faq':
      return (
        <Block name="Content" collapsible={false}>
          <VariantPicker section={section} onChange={onChange} />
          <HeaderFields section={section} onChange={onChange} />
          <ItemList<FaqItem> label="Questions" noun="question" items={section.faqs ?? []} make={() => ({ q: '', a: '' })}
            title={(f) => f.q} onChange={(faqs) => onChange({ faqs })}>
            {(f, _i, set) => (
              <>
                <TextField dynamic label="Question" value={f.q} onChange={(v) => set({ ...f, q: v })} />
                <TextAreaField dynamic label="Answer" value={f.a} onChange={(v) => set({ ...f, a: v })} />
              </>
            )}
          </ItemList>
        </Block>
      );
    case 'steps':
      return (
        <Block name="Content" collapsible={false}>
          <VariantPicker section={section} onChange={onChange} />
          <HeaderFields section={section} onChange={onChange} />
          <ItemList<StepItem> label="Steps" noun="step" items={section.steps ?? []} make={() => ({ label: '', detail: '' })}
            title={(st) => st.label} summary={(st) => st.detail} onChange={(steps) => onChange({ steps })}>
            {(st, _i, set) => (
              <>
                <TextField dynamic label="Title" value={st.label} onChange={(v) => set({ ...st, label: v })} />
                <TextAreaField dynamic label="Detail" value={st.detail ?? ''} onChange={(v) => set({ ...st, detail: v })} />
              </>
            )}
          </ItemList>
        </Block>
      );
    case 'logos':
      return (
        <Block name="Content" collapsible={false}>
          <HeaderFields section={section} onChange={onChange} />
          <ItemList<string> label="Logos" noun="logo" items={section.logos ?? []} make={() => ''}
            title={() => undefined} summary={(url) => fileLabel(url)} thumb={(url) => url}
            onChange={(logos) => onChange({ logos })}>
            {(url, _i, set) => <ImageInput label="Logo" value={url} clearable={false} onChange={set} />}
          </ItemList>
          <Check label="Scroll continuously" checked={!!section.logos_marquee} onChange={(v) => onChange({ logos_marquee: v || undefined })} />
        </Block>
      );
    case 'testimonial':
      return (
        <Block name="Content" collapsible={false}>
          <VariantPicker section={section} onChange={onChange} />
          <TextAreaField dynamic label="Quote" value={section.quote ?? ''} onChange={(v) => onChange({ quote: v })} />
          <div className="grid min-w-0 grid-cols-2 gap-2">
            <TextField dynamic label="Name" value={section.attribution ?? ''} onChange={(v) => onChange({ attribution: v })} />
            <TextField dynamic label="Role or company" value={section.role ?? ''} onChange={(v) => onChange({ role: v })} />
          </div>
          <ImageInput label="Photo" value={section.photo_url ?? ''} onChange={(url) => onChange({ photo_url: url })} />
        </Block>
      );
    case 'image':
      return (
        <Block name="Content" collapsible={false}>
          <ImageInput label="Image" value={section.url ?? ''} onChange={(url) => onChange({ url })} />
          <TextField dynamic label="Description for screen readers" placeholder="What the image shows" value={section.alt ?? ''}
            onChange={(v) => onChange({ alt: v })} />
        </Block>
      );
    case 'embed': {
      const bad = !!section.embed_url && !isHttpsUrl(embedCodeToUrl(section.embed_url)) && !isScriptEmbed(section.embed_url);
      return (
        <Block name="Content" collapsible={false}>
          <TextAreaField label="Embed link or code" mono
            placeholder="A link, or the embed code (<iframe> or <script>) from Wistia, Calendly, Loom, YouTube, Vimeo, Google Maps…"
            value={section.embed_url ?? ''} onChange={(v) => onChange({ embed_url: v })}
            error={bad ? 'Needs a secure https link, or embed code with an <iframe> or a <script>.' : undefined} />
        </Block>
      );
    }
    case 'booking':
      return (
        <Block name="Content" collapsible={false}>
          <BookingPicker value={section.booking_slug ?? ''} onChange={(slug) => onChange({ booking_slug: slug })} />
        </Block>
      );
    case 'cta': {
      const href = section.href ?? '';
      const mode = href === '#form' ? '#form' : href === '#next' ? '#next' : 'custom';
      const ctaLinks = section.links ?? [];
      // Stacked link buttons (Linktree style): once any are added they replace
      // the single button and render as a full-width column.
      if (ctaLinks.length > 0) {
        return (
          <Block name="Button" collapsible={false}>
            <LinkList dynamic label="Link buttons" noun="link button" links={ctaLinks} newHref="https://" labelPlaceholder="Button text"
              hrefPlaceholder="https://…" onChange={(links) => onChange({ links })} />
            <AttributionForwardingField section={section} onChange={onChange} />
          </Block>
        );
      }
      return (
        <>
          <Block name="Content" collapsible={false}>
            <TextAreaField dynamic singleLine label="Heading" placeholder={OPTIONAL} value={section.heading ?? ''}
              onChange={(v) => onChange({ heading: v || undefined })} />
            {section.heading && <HighlightField section={section} onChange={onChange} />}
            {section.heading && (
              <TextAreaField dynamic label="Supporting text" placeholder={OPTIONAL} value={section.subheadline ?? ''}
                onChange={(v) => onChange({ subheadline: v || undefined })} />
            )}
          </Block>
          {/* The button is what this section is for: always open. */}
          <Block name="Button" collapsible={false}>
            <TextField dynamic label="Button text" value={section.label ?? ''} onChange={(v) => onChange({ label: v })} />
            <SelectField label="When clicked, go to" value={mode} onChange={(v) => onChange({ href: v === 'custom' ? '' : v })}
              hint={mode === '#next' && !multiStep ? 'This page has one step, so for now the button scrolls to the form.' : undefined}>
              <option value="#form">The form (#form)</option>
              <option value="#next">The next step (#next)</option>
              <option value="custom">Another website…</option>
            </SelectField>
            {mode === 'custom' && <TextField label="Link" placeholder="https://…" value={href} onChange={(v) => onChange({ href: v })} />}
            {mode === 'custom' && <AttributionForwardingField section={section} onChange={onChange} />}
            <NoteField section={section} onChange={onChange} />
            <AddButton onClick={() => onChange({ links: [...ctaLinks, { label: '', href: 'https://' }] })}>
              Use stacked link buttons instead
            </AddButton>
          </Block>
        </>
      );
    }
    case 'footer':
      return (
        <Block name="Content" collapsible={false}>
          <FooterLayoutFields section={section} onChange={onChange} />
          <TextAreaField dynamic label="Footer text" placeholder="e.g. © 2026 Acme Wealth, 123 Main St" value={section.body ?? ''}
            onChange={(v) => onChange({ body: v })} />
          <LinkList dynamic label="Links" links={section.links ?? []} newHref="" labelPlaceholder="e.g. Privacy" hrefPlaceholder="https://… or #form"
            onChange={(links) => onChange({ links })} />
        </Block>
      );
    case 'pricing':
      return (
        <Block name="Content" collapsible={false}>
          <HeaderFields section={section} onChange={onChange} />
          <ItemList<PricingPlan> label="Plans" noun="plan" items={section.plans ?? []}
            make={() => ({ name: '', price: '', features: [] })}
            title={(p) => p.name} summary={(p) => `${p.price}${p.period ?? ''}`}
            onChange={(plans) => onChange({ plans })}>
            {(plan, _i, set) => {
              const p = (x: Partial<PricingPlan>) => set({ ...plan, ...x });
              return (
                <>
                  <TextField dynamic label="Plan name" placeholder="e.g. Starter" value={plan.name} onChange={(v) => p({ name: v })} />
                  <TextField dynamic label="Description" placeholder={OPTIONAL} value={plan.description ?? ''}
                    onChange={(v) => p({ description: v || undefined })} />
                  <div className="grid min-w-0 grid-cols-3 gap-2">
                    <TextField dynamic label="Price" placeholder="$49" value={plan.price} onChange={(v) => p({ price: v })} />
                    <TextField dynamic label="Was" placeholder="$99" value={plan.price_compare ?? ''} onChange={(v) => p({ price_compare: v || undefined })} />
                    <TextField dynamic label="Period" placeholder="/month" value={plan.period ?? ''} onChange={(v) => p({ period: v })} />
                  </div>
                  <TextAreaField dynamic label="Features" placeholder="One per line" value={(plan.features ?? []).join('\n')}
                    onChange={(v) => p({ features: v.split('\n') })} />
                  <TextField dynamic label="Button text" placeholder="e.g. Choose Starter" value={plan.cta_label ?? ''} onChange={(v) => p({ cta_label: v })} />
                  <TextField label="When clicked, go to" placeholder="#form if empty" value={plan.cta_href ?? ''} onChange={(v) => p({ cta_href: v })} />
                  <Check label="Highlight this plan" checked={plan.highlight ?? false} onChange={(v) => p({ highlight: v })} />
                  {plan.highlight && (
                    <TextField dynamic label="Ribbon text" placeholder="Most popular" value={plan.badge ?? ''} onChange={(v) => p({ badge: v || undefined })} />
                  )}
                </>
              );
            }}
          </ItemList>
          <AttributionForwardingField section={section} onChange={onChange} />
        </Block>
      );
    case 'video': {
      const bad = !!section.video_url && !isHttpsUrl(embedCodeToUrl(section.video_url));
      return (
        <Block name="Content" collapsible={false}>
          <TextAreaField dynamic singleLine label="Heading" placeholder={OPTIONAL} value={section.heading ?? ''} onChange={(v) => onChange({ heading: v })} />
          <TextField label="Video link" placeholder="YouTube, Loom, Vimeo or Wistia link" value={section.video_url ?? ''}
            error={bad ? 'Needs a secure https link.' : undefined} onChange={(v) => onChange({ video_url: v })} />
          <ImageInput label="Cover image" value={section.poster_url ?? ''}
            onChange={(url) => onChange({ poster_url: url || undefined })} />
        </Block>
      );
    }
    case 'gallery':
      return (
        <Block name="Content" collapsible={false}>
          <HeaderFields section={section} onChange={onChange} />
          <ItemList<string> label="Images" noun="image" items={section.images ?? []} make={() => ''}
            title={() => undefined} summary={(url) => fileLabel(url)} thumb={(url) => url}
            onChange={(images) => onChange({ images })}>
            {(url, _i, set) => <ImageInput label="Image" value={url} clearable={false} onChange={set} />}
          </ItemList>
        </Block>
      );
    case 'team':
      return (
        <Block name="Content" collapsible={false}>
          <HeaderFields section={section} onChange={onChange} />
          <ItemList<TeamMember> label="People" noun="person" items={section.members ?? []} make={() => ({ name: '' })}
            title={(m) => m.name} summary={(m) => m.role} thumb={(m) => m.photo_url}
            onChange={(members) => onChange({ members })}>
            {(m, _i, set) => {
              const p = (x: Partial<TeamMember>) => set({ ...m, ...x });
              return (
                <>
                  <div className="grid min-w-0 grid-cols-2 gap-2">
                    <TextField dynamic label="Name" value={m.name} onChange={(v) => p({ name: v })} />
                    <TextField dynamic label="Role" value={m.role ?? ''} onChange={(v) => p({ role: v })} />
                  </div>
                  <ImageInput label="Photo" value={m.photo_url ?? ''} onChange={(url) => p({ photo_url: url })} />
                </>
              );
            }}
          </ItemList>
        </Block>
      );
    case 'guarantee':
      return (
        <Block name="Content" collapsible={false}>
          <TextAreaField dynamic singleLine label="Heading" placeholder="e.g. Our no-risk promise" value={section.heading ?? ''} onChange={(v) => onChange({ heading: v })} />
          <TextAreaField dynamic label="Promise" placeholder="What you promise, in plain words" value={section.body ?? ''}
            onChange={(v) => onChange({ body: v })} />
        </Block>
      );
    case 'divider':
      return (
        <Block name="Content" collapsible={false}>
          <Choice label="Size" value={section.size ?? 'md'} options={DIVIDER_SIZES} onChange={(z) => onChange({ size: z })} />
          <Check label="Show line" checked={section.line ?? false} onChange={(v) => onChange({ line: v })} />
        </Block>
      );
    case 'countdown':
      return (
        <Block name="Content" collapsible={false}>
          <TextAreaField dynamic singleLine label="Heading" placeholder={OPTIONAL} value={section.heading ?? ''} onChange={(v) => onChange({ heading: v })} />
          <TextField label="Deadline (Eastern time)" type="datetime-local" value={isoToEtWall(section.deadline)}
            onChange={(v) => onChange({ deadline: v ? etWallToIso(v) : '' })} />
          <TextField dynamic label="After the deadline" placeholder="e.g. This offer has ended." value={section.expired_text ?? ''}
            hint="Replaces the timer. Leave it empty to hide the timer." onChange={(v) => onChange({ expired_text: v })} />
        </Block>
      );
    case 'form': {
      const layout = section.layout === 'split' || section.layout === 'stacked' ? section.layout : 'card';
      return (
        <Block name="Content" collapsible={false}>
          <SelectField label="Form layout" className="[&>select]:min-h-11" value={layout} onChange={(v) => onChange({ layout: v as Section['layout'] })}>
            {FORM_LAYOUTS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
          </SelectField>
          {layout === 'split' ? (
            <div className="[&_button]:min-h-11"><Choice label="Column sizes" value={section.form_ratio === 'wide' ? 'wide' : 'equal'}
              options={[[ 'equal', 'Equal' ], [ 'wide', 'Larger form' ]] as const}
              onChange={(form_ratio) => onChange({ form_ratio })} /></div>
          ) : (
            <SelectField label="Form width" className="[&>select]:min-h-11" value={section.form_width === 'wide' || section.form_width === 'full' ? section.form_width : 'standard'}
              onChange={(v) => onChange({ form_width: v as Section['form_width'] })}>
              <option value="standard">Standard</option>
              <option value="wide">Wide</option>
              <option value="full">Full width</option>
            </SelectField>
          )}
          {layout !== 'card' && (
            <>
              <TextAreaField dynamic singleLine label="Heading" placeholder={layout === 'stacked' ? 'Shows above the form' : 'Shows beside the form'} value={section.headline ?? ''}
                onChange={(v) => onChange({ headline: v })} />
              <TextAreaField dynamic label="Supporting text" placeholder={OPTIONAL} value={section.subheadline ?? ''} onChange={(v) => onChange({ subheadline: v })} />
              <StringList dynamic label="Bullet points" noun="point" items={section.items ?? []} onChange={(items) => onChange({ items })} />
              {layout === 'split' && <Choice label="Form side" value={section.form_side ?? 'right'} options={SIDES}
                onChange={(v) => onChange({ form_side: v })} />}
            </>
          )}
        </Block>
      );
    }
    case 'split':
      return (
        <>
          <Block name="Content" collapsible={false}>
            <TextField dynamic label="Small label above" placeholder={OPTIONAL} value={section.eyebrow ?? ''} onChange={(v) => onChange({ eyebrow: v })} />
            <TextAreaField dynamic singleLine label="Heading" value={section.heading ?? ''} onChange={(v) => onChange({ heading: v })} />
            {section.heading && <HighlightField section={section} onChange={onChange} />}
            <TextAreaField dynamic label="Text" value={section.body ?? ''} onChange={(v) => onChange({ body: v })} />
            <StringList dynamic label="Check list" noun="item" items={section.items ?? []} onChange={(items) => onChange({ items })} />
          </Block>
          <Block name="Media" defaultOpen>
            <ImageInput label="Image" value={section.image_url ?? ''} onChange={(url) => onChange({ image_url: url })} />
            <Choice label="Image side" value={section.image_side ?? 'left'} options={SIDES}
              onChange={(sd) => onChange({ image_side: sd })} />
          </Block>
          <Block name="Button" defaultOpen>
            <TextField dynamic label="Button text" placeholder={OPTIONAL} value={section.label ?? ''} onChange={(v) => onChange({ label: v })} />
            {section.label && (
              <>
                <SplitButtonAction section={section} onChange={onChange} />
                <NoteField section={section} onChange={onChange} />
                <AttributionForwardingField section={section} onChange={onChange} />
              </>
            )}
          </Block>
        </>
      );
    case 'nav':
      return <NavFields section={section} onChange={onChange} />;
    case 'banner':
      return <BannerFields section={section} onChange={onChange} />;
    case 'testimonials':
      return (
        <Block name="Content" collapsible={false}>
          <VariantPicker section={section} onChange={onChange} />
          <HeaderFields section={section} onChange={onChange} />
          <ReviewsFields section={section} onChange={onChange} />
        </Block>
      );
    case 'modules':
      return (
        <Block name="Content" collapsible={false}>
          <VariantPicker section={section} onChange={onChange} />
          <HeaderFields section={section} onChange={onChange} />
          <ModulesFields section={section} onChange={onChange} />
        </Block>
      );
    case 'bio':
      return <BioFields section={section} onChange={onChange} />;
    case 'offer':
      return <OfferFields section={section} onChange={onChange} />;
    default:
      return null;
  }
}
